import { useQuery } from "@tanstack/react-query"
import {
  Link,
  useLocation,
  useMatchRoute,
  useRouterState,
} from "@tanstack/react-router"
import { useEffect } from "react"
import {
  Dialog,
  ModalOverlay,
  ModalOverlayProps,
  Modal as RACModal,
} from "react-aria-components"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { IconName } from "@/assets/icons"
import { Button, IconButton, LinkButton } from "@/globalComponents/atoms/Button"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Separator from "@/globalComponents/atoms/Separator"
import Text from "@/globalComponents/atoms/Text"
import LanguageSwitcherModal from "@/globalComponents/molecules/LanguageSwitcherModal"
import { ENV } from "@/utilities/config"
import { upperFirst } from "@/utilities/etc"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import { useTheme } from "@/utilities/hooks/useTheme"
import useToggle from "@/utilities/hooks/useToggle"
import { getLanguageName, iconForLanguage } from "@/utilities/localization"

import styles from "./Drawer.module.css"

export function Drawer(props: ModalOverlayProps) {
  const { t, i18n } = useTranslation()
  const { scannedArea, documentThemeMode } = useTheme()
  const { data: me } = useQuery(meQueryOptions())
  const routerState = useRouterState()
  const location = useLocation()
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useToggle(false)

  const matchRoute = useMatchRoute()
  const isVehicleRoute = !!matchRoute({ to: "/account/vehicles", fuzzy: true })
  const isPassesRoute = !!matchRoute({ to: "/account/passes", fuzzy: true })
  const isReservationsRoute = !!matchRoute({
    to: "/account/reservations",
    fuzzy: true,
  })
  const isMembershipsRoute = !!matchRoute({
    to: "/account/memberships",
    fuzzy: true,
  })

  const hasFlexPurchase = scannedArea?.theme.welcome.flowCard.flows.some(
    (flow) => flow.type === "flexPurchase"
  )
  const hasReserve = scannedArea?.theme.welcome.flowCard.flows.some(
    (flow) => flow.type === "reserve"
  )

  useEffect(() => {
    if (routerState.status === "pending" && props.isOpen === true) {
      props.onOpenChange?.(false)
    }
  }, [routerState.status, props.isOpen])

  return (
    <ModalOverlay className="nav-drawer--overlay" isDismissable {...props}>
      <RACModal className="nav-drawer">
        <Dialog className="nav-drawer--inner">
          <div className="nav-drawer--header">
            <Heading level={2} slot="title">
              {me?.cognitoUser
                ? t("navigation.drawer.authenticatedHeader", {
                    firstName: me.cognitoUser.given_name,
                  })
                : t("navigation.drawer.header")}
            </Heading>
            <IconButton
              buttonType="primary"
              presentation="ghost"
              iconName="close"
              aria-label={t("common.close")}
              onPress={() => props.onOpenChange?.(false)}
            />
          </div>
          <div className="nav-drawer--links">
            <ul>
              {documentThemeMode === "whiteLabel" && scannedArea?.qrLookupID ? (
                <li className="body--1">
                  <Link
                    to="/qr/$qrLookupID/welcome"
                    params={{ qrLookupID: scannedArea.qrLookupID }}
                  >
                    <Icon name="home" slot="left" />
                    {t("common.home")}
                  </Link>
                </li>
              ) : (
                <li className="body--1">
                  <Link to="/">
                    <Icon name="home" slot="left" />
                    {t("common.home")}
                  </Link>
                </li>
              )}
              {documentThemeMode === "whiteLabel" &&
                scannedArea?.theme.drawer?.externalLinks?.map((link, index) => (
                  <li className="body--1" key={index}>
                    {link.type === "rates" ? (
                      <a href={link.link} target="_blank" rel="noreferrer">
                        <Icon name={link.icon as IconName} slot="left" />
                        {t("navigation.drawer.rates")}
                      </a>
                    ) : (
                      <></>
                    )}
                  </li>
                ))}
              <li className="body--1">
                {documentThemeMode === "whiteLabel" && scannedArea ? (
                  <Link
                    to="/qr/$qrLookupID/help"
                    params={{ qrLookupID: scannedArea?.qrLookupID }}
                  >
                    <Icon name="question" slot="left" />
                    {t("navigation.drawer.help")}
                  </Link>
                ) : (
                  <Link to="/help">
                    <Icon name="question" slot="left" />
                    {t("navigation.drawer.help")}
                  </Link>
                )}
              </li>
              <li className="body--1">
                {documentThemeMode === "whiteLabel" && scannedArea ? (
                  <Link
                    to="/qr/$qrLookupID/policies"
                    params={{ qrLookupID: scannedArea.qrLookupID }}
                  >
                    <Icon name="file" slot="left" />
                    {t("navigation.drawer.policies")}
                  </Link>
                ) : (
                  <Link to="/policies">
                    <Icon name="file" slot="left" />
                    {t("navigation.drawer.policies")}
                  </Link>
                )}
              </li>
              <li className="body--1">
                <button
                  onClick={() => setIsLanguageModalOpen()}
                  className={styles.languageButton}
                >
                  <Icon name="translation" />
                  <span className={styles.languageButtonText}>
                    {t("navigation.drawer.languages")}
                  </span>
                  <Text level={3} className={styles.languageButtonName}>
                    {getLanguageName(i18n.resolvedLanguage)}
                  </Text>
                  <Icon
                    name={iconForLanguage(i18n.resolvedLanguage)}
                    retainOriginalColors={
                      iconForLanguage(i18n.resolvedLanguage) !== "translation"
                    }
                    className={styles.languageButtonFlag}
                  />
                </button>
              </li>
            </ul>
            {me?.cognitoUser && (
              <>
                <Separator
                  orientation="horizontal"
                  style={{ marginLeft: "var(--space-4)" }}
                />
                <ul>
                  <li className="body--1">
                    <Link
                      to="/account"
                      search={{
                        qrLookupID:
                          documentThemeMode === "whiteLabel" && scannedArea
                            ? scannedArea?.qrLookupID
                            : undefined,
                      }}
                      activeOptions={{
                        exact:
                          isVehicleRoute ||
                          isPassesRoute ||
                          isReservationsRoute ||
                          isMembershipsRoute,
                      }}
                    >
                      <Icon name="person" slot="left" />
                      {t("navigation.drawer.account")}
                    </Link>
                  </li>
                  {hasFlexPurchase && (
                    <li className="body--1">
                      <Link
                        to="/account/passes"
                        search={{
                          qrLookupID:
                            documentThemeMode === "whiteLabel" && scannedArea
                              ? scannedArea?.qrLookupID
                              : undefined,
                        }}
                      >
                        <Icon name="ticket" slot="left" />
                        {t("navigation.drawer.passes")}
                      </Link>
                    </li>
                  )}
                  <li className="body--1">
                    <Link
                      to="/account/vehicles"
                      search={{
                        qrLookupID:
                          documentThemeMode === "whiteLabel" && scannedArea
                            ? scannedArea?.qrLookupID
                            : undefined,
                      }}
                    >
                      <Icon name="car" slot="left" />
                      {t("navigation.drawer.vehicles")}
                    </Link>
                  </li>
                  {hasReserve && (
                    <li className="body--1">
                      <Link
                        to="/account/reservations"
                        search={{
                          qrLookupID:
                            documentThemeMode === "whiteLabel" && scannedArea
                              ? scannedArea?.qrLookupID
                              : undefined,
                        }}
                      >
                        <Icon name="calendar" slot="left" />
                        {t("navigation.drawer.reservations")}
                      </Link>
                    </li>
                  )}
                  {hasReserve && (
                    <li className="body--1">
                      <Link
                        to="/account/memberships"
                        search={{
                          qrLookupID:
                            documentThemeMode === "whiteLabel" && scannedArea
                              ? scannedArea?.qrLookupID
                              : undefined,
                        }}
                      >
                        <Icon name="key" slot="left" />
                        {t("navigation.drawer.keysAndMemberships")}
                      </Link>
                    </li>
                  )}
                </ul>
              </>
            )}
          </div>
          {
            <div className="nav-drawer--footer">
              {me?.cognitoUser ? (
                <Button
                  onPress={() => api.auth.signOut()}
                  buttonType="danger"
                  presentation="ghost"
                >
                  <Icon name="logOut" slot="left" />
                  {t("navigation.drawer.signOut")}
                </Button>
              ) : (
                <>
                  <LinkButton
                    buttonType="default"
                    to="/auth/sign-in"
                    search={{
                      redirect: location.href,
                      qrLookupID:
                        documentThemeMode === "whiteLabel" && scannedArea
                          ? scannedArea?.qrLookupID
                          : undefined,
                      plate: undefined,
                      phone: undefined,
                      email: undefined,
                      conversionFlow: undefined,
                    }}
                  >
                    {t("navigation.drawer.signIn")}
                  </LinkButton>
                  <LinkButton
                    buttonType="primary"
                    to="/auth/create-account"
                    search={{
                      redirect: location.href,
                      qrLookupID:
                        documentThemeMode === "whiteLabel" && scannedArea
                          ? scannedArea?.qrLookupID
                          : undefined,
                      plate: undefined,
                      phone: undefined,
                      email: undefined,
                      conversionFlow: undefined,
                    }}
                  >
                    {t("navigation.drawer.createAccount")}
                  </LinkButton>
                </>
              )}
            </div>
          }
          {me && (
            <Text
              level={3}
              className="nav-drawer--app-version"
              style={{ paddingBottom: "var(--space-1)" }}
            >
              {t("navigation.drawer.loggedInAs", {
                email: me.cognitoUser.email,
              })}
            </Text>
          )}
          <Text level={3} className="nav-drawer--app-version">
            {ENV !== "production" && `${upperFirst(ENV)} `}Version {APP_VERSION}
          </Text>
        </Dialog>
      </RACModal>
      <LanguageSwitcherModal
        isOpen={isLanguageModalOpen}
        onOpenChange={setIsLanguageModalOpen}
      />
    </ModalOverlay>
  )
}

export default Drawer
